import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { useRecoilState } from "recoil";

import {
  FileInput,
  MultiSelect,
  Paper,
  PasswordInput,
  rem,
  Select,
  TextInput,
} from "@mantine/core";

import { IconUpload } from "@tabler/icons-react";
import { groupListState } from "components/Form/atom/groupListState";
import { getCombo } from "pages/Admin/User/apis/adminUserApis";
import styled from "styled-components";
import NaverMapPicker from "./NaverMapPicker";

function DeviceForm(props) {
  const { fields, onSubmit, submitButton, defaultValues } = props;
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const lat = watch("map_ycord");
  const lng = watch("map_xcord");

  useEffect(() => {
    getCombo().then((res) => {
      setGroupList(
        res
          .filter((item) => item.id === "group")
          .map((item) => ({
            value: item.code,
            label: item.name,
          }))
      );
    });
  }, []);

  const [groupList, setGroupList] = useRecoilState(groupListState);

  const validateID = (value) => {
    const idRegex = /^[a-zA-Z0-9]+$/;
    if (typeof value === "undefined" || !idRegex.test(value)) {
      return "영문자와 숫자만 사용 가능합니다.";
    }
  };

  const validatePassword = (value) => {
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;
    if (!passwordRegex.test(value)) {
      return "알파벳,숫자,특수문자로 이루어진 8자 이상의 패스워드가 필요합니다.";
    }
  };

  const validateConfirmPassword = (value) => {
    const password = getValues("password");
    if (value !== password) {
      return "비밀번호와 일치하지 않습니다";
    }
  };

  const validateInput = (value) => {
    const inputRegex = /.+/;
    if (typeof value === "undefined" || !inputRegex.test(value)) {
      return "Text를 입력해주세요.";
    }
  };

  const validateAddress = (value) => {
    if (typeof value === "undefined" || !value) {
      return "주소를 입력해주세요.";
    }
    if (value.length < 6) {
      return "주소는 최소 6자 이상 입력해주세요.";
    }
  };

  const validateSelect = (value) => {
    if (!value) {
      return "하나를 선택해주세요.";
    }
  };

  const validateFile = (value) => {
    if (!value) {
      return "파일을 업로드해주세요.";
    }
    return undefined;
  };

  return (
    <Paper shadow="xs" p="md" w="100%">
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: "100%" }}
      >
        <Styled.Grid>
          {fields?.map((field, index) => {
            const {
              key,
              name,
              type,
              options,
              component: Component,
              disabled,
              defaultValue,
              fullWidth,
              validate: customValidate,
            } = field;
            const inputId = `device-${key}`;
            return (
              <Styled.Field key={index} data-full={fullWidth}>
                <Styled.Label htmlFor={inputId}>{name}</Styled.Label>
                <Styled.InputArea>
                  {type === "text" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: customValidate || validateInput }}
                      render={({ field }) => (
                        <TextInput
                          id={inputId}
                          error={errors[key]?.message}
                          disabled={disabled}
                          required
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  )}
                  {type === "id" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validateID }}
                      render={({ field }) => (
                        <TextInput
                          id={inputId}
                          error={errors[key]?.message}
                          disabled={disabled}
                          required
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  )}
                  {type === "password" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validatePassword }}
                      render={({ field }) => (
                        <PasswordInput
                          id={inputId}
                          error={errors[key]?.message}
                          required
                          {...field}
                          value={field.value ?? ""}
                          onChange={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  )}
                  {type === "confirm_password" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validateConfirmPassword }}
                      render={({ field }) => (
                        <PasswordInput
                          id={inputId}
                          error={errors[key]?.message}
                          required
                          {...field}
                          value={field.value ?? ""}
                          onChange={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  )}
                  {type === "file" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validateFile }}
                      render={({ field }) => (
                        <FileInput
                          id={inputId}
                          placeholder="파일을 업로드 해주세요."
                          error={errors[key]?.message}
                          icon={<IconUpload size={rem(14)} />}
                          required
                          {...field}
                          value={field.value ?? null}
                          onChange={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  )}
                  {type === "phone_number" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validateInput }}
                      render={({ field }) => (
                        <TextInput
                          id={inputId}
                          error={errors[key]?.message}
                          required
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  )}
                  {type === "select" && (
                    <Controller
                      control={control}
                      name={key}
                      rules={{ validate: validateSelect }}
                      defaultValue={defaultValue}
                      render={({ field }) => (
                        <Select
                          id={inputId}
                          placeholder={`${name}을 골라주세요.`}
                          error={errors[key]?.message}
                          data={options}
                          required
                          disabled={disabled}
                          {...field}
                          value={field.value ?? null}
                          onChange={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  )}
                  {type === "multi_select" && (
                    <Controller
                      control={control}
                      name={key}
                      render={({ field }) => (
                        <MultiSelect
                          id={inputId}
                          error={errors[key]?.message}
                          data={options}
                          required
                          {...field}
                          value={Array.isArray(field.value) ? field.value : []}
                          onChange={(val) => field.onChange(val)}
                        />
                      )}
                    />
                  )}
                  {type === "etc" && (
                    <Controller
                      control={control}
                      name={key}
                      render={({ field }) => (
                        <Component
                          field={field}
                          inputId={inputId}
                          onChange={(event) => field.onChange(event)}
                          value={field.value ?? ""}
                        />
                      )}
                    />
                  )}
                  {type === "map_picker" && lat != null && lng != null && (
                    <Controller
                      control={control}
                      key={key}
                      name={key}
                      render={({ field }) => (
                        <NaverMapPicker
                          value={{lat, lng}}
                          lat={lat}
                          lng={lng}
                          onChange={(pos) => {
                            field.onChange(pos);
                            setValue("map_xcord", pos.lng);
                            setValue("map_ycord", pos.lat);
                          }}
                        />
                      )}
                    />
                  )}
                </Styled.InputArea>
              </Styled.Field>
            );
          })}
          {submitButton && (
            <Styled.ButtonWrapper data-full="true">
              {submitButton}
            </Styled.ButtonWrapper>
          )}
        </Styled.Grid>
      </form>
    </Paper>
  );
}

export default DeviceForm;

const Styled = {
  Grid: styled.div`
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: min-content;
    gap: 8px 16px;
    overflow: visible;

    & > div[data-full="true"] {
      grid-column: 1 / -1;
    }
  `,
  Field: styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
  `,
  Label: styled.label`
    width: 140px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #343a40;
    line-height: 36px;
    text-align: right;
  `,
  InputArea: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  ButtonWrapper: styled.div`
    display: flex;
    justify-content: center;
    margin-top: 8px;
  `,
};
